import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Menu, X, Globe } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import BloodflowMark from '../../Assets/BloodflowLogoNoLetters.png';

const Navbar = ({ isAnnouncementVisible }) => {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  const currentLanguage = i18n.language?.startsWith('pt') ? 'PT' : 'EN';

  const toggleLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setIsLangMenuOpen(false);
  };

  const navItems = useMemo(() => [
    { label: t('navbar.inside', 'Inside your EMR'), href: '#emr-showcase' },
    { label: t('navbar.how', 'How it works'), href: '#how' },
    { label: t('navbar.backed', 'Backed by'), href: '#investment' },
  ], [t]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(i => i.href.substring(1));
      for (const section of [...sections].reverse()) {
        const el = document.getElementById(section);
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveSection(section);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  return (
    <>
      <nav
        className={`fixed left-0 right-0 z-50 border-b bf-nav-editorial ${isAnnouncementVisible ? 'top-10' : 'top-0'}`}
        style={{
          backdropFilter: 'blur(16px) saturate(1.4)',
          WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
          background: 'rgba(10, 12, 15, 0.9)',
          borderColor: 'var(--bf-rule)',
          fontFamily: 'var(--bf-sans)',
        }}
      >
        <div className="bf-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 96 }}>
          {/* Brand */}
          <RouterLink
            to="/"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              textDecoration: 'none',
              color: 'var(--bf-ink)',
            }}
            aria-label="BloodFlow"
          >
            <img
              src={BloodflowMark}
              alt=""
              style={{ height: 68, width: 'auto', display: 'block' }}
            />
            <span
              style={{
                fontFamily: 'var(--bf-sans)',
                fontSize: 24,
                fontWeight: 650,
                letterSpacing: '-0.03em',
                color: 'var(--bf-ink)',
                lineHeight: 1,
              }}
            >
              BloodFlow
            </span>
          </RouterLink>

          {/* Desktop nav links */}
          <div className="hidden lg:flex" style={{ gap: 32, fontSize: 14 }}>
            {navItems.map(item => {
              const isActive = activeSection === item.href.substring(1);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  style={{
                    color: isActive ? 'var(--bf-ink)' : 'var(--bf-muted)',
                    textDecoration: 'none',
                    transition: 'color 0.15s',
                    position: 'relative',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--bf-ink)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isActive ? 'var(--bf-ink)' : 'var(--bf-muted)')}
                >
                  {item.label}
                </a>
              );
            })}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex" style={{ gap: 10, alignItems: 'center' }}>
            {/* Language switcher */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setIsLangMenuOpen(o => !o)}
                className="bf-small"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 10px',
                  background: 'transparent',
                  border: '1px solid transparent',
                  color: 'var(--bf-muted)',
                  cursor: 'pointer',
                }}
              >
                <Globe className="w-4 h-4" />
                <span>{currentLanguage}</span>
              </button>
              <AnimatePresence>
                {isLangMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.12 }}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 8,
                      background: 'var(--bf-surface)',
                      border: '1px solid var(--bf-rule-2)',
                      borderRadius: 10,
                      overflow: 'hidden',
                      boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
                      minWidth: 140,
                    }}
                  >
                    <button
                      onClick={() => toggleLanguage('en')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '10px 12px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: 13,
                        color: currentLanguage === 'EN' ? 'var(--bf-accent)' : 'var(--bf-ink)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span>🇬🇧</span> English
                    </button>
                    <button
                      onClick={() => toggleLanguage('pt')}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        padding: '10px 12px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: 13,
                        color: currentLanguage === 'PT' ? 'var(--bf-accent)' : 'var(--bf-ink)',
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <span>🇵🇹</span> Português
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <RouterLink to="/contact-sales" className="bf-btn bf-btn-ghost">
              {t('navbar.contactSales', 'Contact sales')}
            </RouterLink>

            <a
              href="https://calendly.com/tiago-costa-bloodflow"
              target="_blank"
              rel="noopener noreferrer"
              className="bf-btn bf-btn-primary"
            >
              {t('navbar.scheduleDemo', 'Schedule demo')} <span className="bf-arrow">→</span>
            </a>
          </div>

          {/* Mobile trigger */}
          <button
            className="lg:hidden"
            onClick={() => setIsMobileMenuOpen(o => !o)}
            aria-label="Toggle menu"
            style={{ background: 'transparent', border: 'none', color: 'var(--bf-ink)', padding: 8, cursor: 'pointer' }}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden"
          >
            <div
              onClick={() => setIsMobileMenuOpen(false)}
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)' }}
            />
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'absolute',
                top: 108,
                left: 16,
                right: 16,
                background: 'var(--bf-surface)',
                border: '1px solid var(--bf-rule-2)',
                borderRadius: 14,
                padding: 20,
                boxShadow: '0 24px 70px rgba(0,0,0,0.7)',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {navItems.map(item => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    style={{
                      display: 'block',
                      padding: '12px 14px',
                      color: 'var(--bf-ink)',
                      textDecoration: 'none',
                      fontSize: 15,
                      borderRadius: 3,
                    }}
                  >
                    {item.label}
                  </a>
                ))}
                <div style={{ display: 'flex', gap: 8, marginTop: 14, padding: '0 14px', alignItems: 'center' }}>
                  <Globe className="w-4 h-4" style={{ color: 'var(--bf-muted)' }} />
                  <button
                    onClick={() => { toggleLanguage('en'); setIsMobileMenuOpen(false); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 8px',
                      fontSize: 12,
                      color: currentLanguage === 'EN' ? 'var(--bf-accent)' : 'var(--bf-muted)',
                      cursor: 'pointer',
                    }}
                  >🇬🇧 EN</button>
                  <button
                    onClick={() => { toggleLanguage('pt'); setIsMobileMenuOpen(false); }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 8px',
                      fontSize: 12,
                      color: currentLanguage === 'PT' ? 'var(--bf-accent)' : 'var(--bf-muted)',
                      cursor: 'pointer',
                    }}
                  >🇵🇹 PT</button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bf-rule)' }}>
                  <a
                    href="https://calendly.com/tiago-costa-bloodflow"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bf-btn bf-btn-primary"
                    style={{ justifyContent: 'center' }}
                  >
                    {t('navbar.scheduleDemo', 'Schedule demo')} <span className="bf-arrow">→</span>
                  </a>
                  <RouterLink
                    to="/contact-sales"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="bf-btn bf-btn-ghost"
                    style={{ justifyContent: 'center' }}
                  >
                    {t('navbar.contactSales', 'Contact sales')}
                  </RouterLink>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
